import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { AuthService } from '../http-service/auth.service';
import { Router, ActivationEnd, NavigationEnd } from '@angular/router';
import { HttpCancelService } from './http-cancel.service';
import { takeUntil, tap } from 'rxjs/operators';
import { CookieService } from '@vex/services/cookie.service';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { HelperService } from '@vex/services/helper.service';
import { environment } from 'environments/environment';

declare var $;
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly anonymousAccountRoutes = [
    'Account/Login',
    'Account/DiscoverLoginOptions',
    'Account/ResolveDirectoryLogin',
    'Account/Directories',
    'Account/ForgotPassword',
    'Account/ResetPassword',
    'Account/SendConfirmationSms',
    'Account/VerifyCode',
    'Account/RefreshToken',
    'Account/CheckEmail',
    'Account/CheckPhoneNumber',
    'Account/CheckUsername',
    'Account/RegisterUser',
    'Account/ConfirmWithCode'
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private cookie: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private httpCancelService: HttpCancelService,
    private toastr: ToastrService
  ) {
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.dialog.closeAll();
        this.toastr.clear();
        this.helperService.loadingSpinner = false;
        $('#nodeTooltip').remove();
      }

      // An event triggered at the end of the activation part of the Resolve phase of routing.
      if (event instanceof ActivationEnd) {
        // Cancel pending calls
        this.httpCancelService.cancelPendingRequests();

        if (event.snapshot) {
          if (event.snapshot.routeConfig && event.snapshot.routeConfig.path) {
            let path = '';
            path =
              path === ''
                ? event.snapshot.routeConfig.path
                : [event.snapshot.routeConfig.path, path].join('/');
            if (!path.includes('login')) {
            }
          }
        }
      }
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Page navigation cancels view-scoped API calls, but platform-data sync is
    // application-scoped and owns a durable IndexedDB cursor. Consume this
    // internal marker before transmission and allow the request to finish while
    // the user navigates between pages.
    const isBackgroundSyncRequest = req.headers.has('X-Kumorai-Background-Sync');
    if (isBackgroundSyncRequest) {
      req = req.clone({ headers: req.headers.delete('X-Kumorai-Background-Sync') });
    }
    // FastAPI generation endpoints use the same signed user identity as Serverless.
    // Forward the existing login token; never create or persist a second browser token.
    const isProtectedPlatformRequest = req.url.startsWith(environment.API_URL) ||
      req.url.startsWith(environment.WORKER_URL);
    const isAnonymousRequest = this.anonymousAccountRoutes.some(route => req.url.includes(route));
    const isPrivilegedStepUpRequest = req.url.includes('GlobalConsole/V2/Access/Verify') ||
      req.url.includes('GlobalConsoleV2Verify');
    if (isProtectedPlatformRequest && !isAnonymousRequest) {
      const authToken = localStorage.getItem('token');
      if (!this.hasRequiredIdentityClaims(authToken)) {
        // Do not send a protected request that the Function host can only turn into a 500 from its
        // invocation filter. Cancel the current tab's outstanding work and expire the shared session.
        this.httpCancelService.cancelPendingRequests();
        this.authService.afterSessionExpired();
        return throwError(new HttpErrorResponse({
          status: 401,
          statusText: 'Authentication required',
          url: req.url,
          error: { message: 'Your session is no longer valid. Please sign in again.' }
        }));
      }

      req = req.clone({
        setHeaders: { Authorization: 'Bearer ' + authToken }
      });
    }

    if (req.url.includes('Account/SendConfirmationSms') || req.url.includes('Account/VerifyCode')) {
      const authToken = localStorage.getItem('token');
      req = req.clone({
        setHeaders: { Authorization: 'Bearer ' + authToken }
      });
    }

    const response = isBackgroundSyncRequest
      ? next.handle(req)
      : next.handle(req).pipe(takeUntil(this.httpCancelService.onCancelPendingRequests()));

    return response.pipe(
      // If an error occured in api request or 401
      tap(
        () => {},
        (error: any) => {
          if (error instanceof HttpErrorResponse) {
            if (error.status === 401 && !isPrivilegedStepUpRequest) {
              this.httpCancelService.cancelPendingRequests();
              this.authService.afterSessionExpired();
            } else if (error.status === 400) {
              // API validation contracts use both Message and message. Never pass an object to
              // ngx-toastr because it renders as "[object Object]" and hides the actionable error.
              const responseBody = error?.error;
              const validationMessage = typeof responseBody === 'string'
                ? responseBody
                : responseBody?.Message || responseBody?.message || responseBody?.errorMessage;
              if (validationMessage) {
                this.toastrService.warning(validationMessage);
              }
            } else {
              let errorMsg = '';
              if (error.error instanceof ErrorEvent) {
                // this.toastrService.error(error.error.message, 'Error');
                errorMsg = `Error: ${error.error.message}`;
              } else {
                if (
                  !req.url.includes('Account/Login') &&
                  !req.url.includes('Account/ForgotPassword') &&
                  !req.url.includes('Account/ResetPassword') &&
                  !req.url.includes('Account/SendConfirmationSms')
                ) {
                  let expireToast = this.cookie.getCookie('_Kumorai-Error-Toast');
                  if (!expireToast) {
                    const expire = new Date(Date.now() + 5 * 1000);
                    this.cookie.setCookie({
                      name: '_Kumorai-Error-Toast',
                      value: expire.toUTCString(),
                      expireDate: expire
                    });
                    if (environment.CLIENT_NAME == 'Development') {
                      this.toastrService.error(error.message, 'Error Code: ' + error.status);
                    }
                  }
                }
                errorMsg = `Error Code: ${error.status},  Message: ${error.message}`;
              }
            }
            return;
          }
        }
      )
    );
  }

  /** Confirms that a stored JWT contains the claims required by the server authentication filter. */
  private hasRequiredIdentityClaims(token: string | null): boolean {
    const claims = this.authService.decodeJwtPayload(token || '');
    return !!(claims && claims.UserName && claims.Id && claims.OrganizationId);
  }
}
